'use client'

import React, { useEffect, useState } from 'react'
import { useRouter } from '@tanstack/react-router'
import { AlertCircle, Home, RotateCcw, Copy, ClipboardCheck, Terminal } from 'lucide-react'
import { ErrorBoundary } from 'react-error-boundary'
import { Button } from '@/components/ui/button'
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from '@/components/ui/card'

interface ErrorFallbackProps {
  error: Error
  resetErrorBoundary: () => void
}

function ErrorFallback({ error, resetErrorBoundary }: ErrorFallbackProps) {
  const router = useRouter()
  const [copied, setCopied] = useState(false)
  const correlationId = (error as any).correlationId || 'N/A'

  useEffect(() => {
    // In production, we'd log to Sentry / Datadog
    console.error('Uncaught boundary exception:', error)
  }, [error])

  const copyToClipboard = () => {
    navigator.clipboard.writeText(correlationId)
    setCopied(true)
    setTimeout(() => setCopied(false), 2000)
  }

  return (
    <div className='flex min-h-[100dvh] w-full items-center justify-center bg-muted/30 p-4'>
      <Card className='w-full max-w-lg border-destructive/20 shadow-xl dark:border-destructive/30 bg-card'>
        <CardHeader className='space-y-1.5'>
          <div className='flex items-center gap-3'>
            <div className='rounded-full bg-destructive/10 p-2 text-destructive'>
              <AlertCircle className='h-6 w-6' />
            </div>
            <div>
              <CardTitle className='text-xl font-semibold tracking-tight text-destructive'>
                Application Exception
              </CardTitle>
              <CardDescription className='text-sm text-muted-foreground'>
                An unexpected system state occurred. Our tracking system has logged this transaction.
              </CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent className='space-y-4'>
          <div className='rounded-lg border bg-muted p-4 font-mono text-xs text-muted-foreground max-h-48 overflow-y-auto'>
            <div className='flex items-center gap-1.5 font-semibold text-foreground mb-1.5'>
              <Terminal className='h-3.5 w-3.5' />
              <span>Diagnostic Log:</span>
            </div>
            <p className='break-all text-destructive mb-2'>{error.message}</p>
            {error.stack && (
              <details className='cursor-pointer'>
                <summary className='text-muted-foreground hover:text-foreground select-none transition-colors'>
                  Show stack trace
                </summary>
                <pre className='mt-2 text-[10px] leading-relaxed whitespace-pre-wrap'>{error.stack}</pre>
              </details>
            )}
          </div>

          <div className='flex items-center justify-between rounded-lg border bg-card p-3.5'>
            <div className='space-y-0.5'>
              <span className='text-[10px] uppercase tracking-wider font-semibold text-muted-foreground'>Support Code (Trace ID)</span>
              <div className='font-mono text-xs font-semibold text-foreground select-all'>
                {correlationId}
              </div>
            </div>
            <Button
              variant='outline'
              size='sm'
              className='h-8 gap-1.5'
              onClick={copyToClipboard}
              disabled={correlationId === 'N/A'}
            >
              {copied ? (
                <>
                  <ClipboardCheck className='h-3.5 w-3.5 text-emerald-500' />
                  <span className='text-emerald-500 text-xs font-medium'>Copied!</span>
                </>
              ) : (
                <>
                  <Copy className='h-3.5 w-3.5' />
                  <span className='text-xs font-medium'>Copy</span>
                </>
              )}
            </Button>
          </div>
        </CardContent>
        <CardFooter className='flex flex-col gap-2 sm:flex-row sm:justify-end border-t bg-muted/40 p-4 rounded-b-lg'>
          <Button
            variant='ghost'
            size='sm'
            onClick={() => router.navigate({ to: '/' })}
            className='w-full sm:w-auto'
          >
            <Home className='mr-2 h-4 w-4' />
            Go to Home
          </Button>
          <Button
            variant='outline'
            size='sm'
            onClick={() => window.location.reload()}
            className='w-full sm:w-auto'
          >
            <RotateCcw className='mr-2 h-4 w-4' />
            Reload Page
          </Button>
          <Button 
            variant='default'
            size='sm'
            onClick={resetErrorBoundary}
            className='w-full sm:w-auto bg-destructive hover:bg-destructive/95 text-destructive-foreground font-medium'
          >
            Try Again
          </Button>
        </CardFooter>
      </Card>
    </div>
  )
}

interface AppErrorBoundaryProps {
  children: React.ReactNode
}

export function AppErrorBoundary({ children }: AppErrorBoundaryProps) {
  return (
    <ErrorBoundary
      FallbackComponent={ErrorFallback}
      onError={(error, info) => {
        console.error('Error caught by boundary:', error)
        console.error('Component stack:', info.componentStack)
      }}
      onReset={() => {
        // Clear caches or perform other resetting state
      }}
    >
      {children}
    </ErrorBoundary>
  )
}
